<template>
  <div class="tw-min-h-screen tw-bg-slate-50 tw-py-10">
    <div class="tw-container tw-mx-auto tw-max-w-4xl tw-px-4">
      <!-- Confirmation page (shown after redirect back) -->
      <div
        v-if="confirmView"
        class="tw-max-w-lg tw-mx-auto"
      >
        <!-- Pending -->
        <div
          v-if="confirmView === 'pending'"
          class="tw-bg-white tw-rounded-xl tw-shadow-md tw-p-8 tw-text-center"
        >
          <div class="tw-inline-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-50 tw-mb-4">
            <span class="tw-h-7 tw-w-7 tw-border-2 tw-border-blue-500 tw-border-t-transparent tw-rounded-full tw-animate-spin" />
          </div>
          <h2 class="tw-text-xl tw-font-semibold tw-text-slate-900">
            Processing Payment
          </h2>
          <p class="tw-mt-2 tw-text-sm tw-text-slate-500">
            Your payment is being confirmed. Please wait...
          </p>
        </div>

        <!-- Success -->
        <div
          v-else-if="confirmView === 'success'"
          class="tw-bg-white tw-rounded-xl tw-shadow-md tw-p-8 tw-text-center"
        >
          <div class="tw-mx-auto tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-50 tw-mb-4">
            <svg
              class="tw-h-7 tw-w-7 tw-text-emerald-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 class="tw-text-xl tw-font-semibold tw-text-slate-900">
            Payment Successful
          </h2>
          <p class="tw-mt-2 tw-text-sm tw-text-slate-500">
            Thank you. Your payment has been processed successfully.
          </p>
          <div
            v-if="confirmData"
            class="tw-mt-6 tw-bg-slate-50 tw-rounded-lg tw-p-4 tw-text-left tw-space-y-2"
          >
            <div
              v-if="confirmData.name"
              class="tw-flex tw-justify-between tw-text-sm"
            >
              <span class="tw-text-slate-600">Name:</span>
              <span class="tw-font-medium tw-text-slate-900">{{ confirmData.name }}</span>
            </div>
            <div
              v-if="confirmData.email"
              class="tw-flex tw-justify-between tw-text-sm"
            >
              <span class="tw-text-slate-600">Email:</span>
              <span class="tw-font-medium tw-text-slate-900">{{ confirmData.email }}</span>
            </div>
            <div
              v-if="confirmData.amount"
              class="tw-flex tw-justify-between tw-text-sm"
            >
              <span class="tw-text-slate-600">Amount:</span>
              <span class="tw-font-semibold tw-text-slate-900">{{ formatCurrency(confirmData.amount) }}</span>
            </div>
          </div>
        </div>

        <!-- Failed -->
        <div
          v-else-if="confirmView === 'failed'"
          class="tw-bg-white tw-rounded-xl tw-shadow-md tw-p-8 tw-text-center"
        >
          <div class="tw-mx-auto tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-50 tw-mb-4">
            <svg
              class="tw-h-7 tw-w-7 tw-text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 class="tw-text-xl tw-font-semibold tw-text-slate-900">
            Payment Failed
          </h2>
          <p class="tw-mt-2 tw-text-sm tw-text-slate-500">
            Your payment could not be processed. Please contact support for assistance.
          </p>
        </div>
      </div>

      <!-- Normal payment flow -->
      <template v-else>

      <!-- Page heading -->
      <header class="tw-mb-8">
        <h1 class="tw-text-2xl md:tw-text-3xl tw-font-semibold tw-text-slate-900">
          Make a Payment
        </h1>
        <p class="tw-mt-2 tw-text-sm tw-text-slate-600">
          Enter your order reference and payment amount to complete a secure payment
        </p>
      </header>

      <!-- Main content -->
      <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6">
        <!-- Left: payment details form -->
        <section class="lg:tw-col-span-2 tw-space-y-6">
          <!-- Payment details card -->
          <div class="tw-bg-white tw-rounded-xl tw-shadow-md tw-p-6">
            <h2 class="tw-text-lg tw-font-semibold tw-text-slate-900 tw-mb-4">
              Payment Details
            </h2>

            <div class="tw-space-y-4">
              <!-- Reference field -->
              <div>
                <label
                  for="reference"
                  class="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1"
                >
                  Order Reference <span class="tw-text-red-500">*</span>
                </label>
                <input
                  id="reference"
                  v-model="reference"
                  type="text"
                  placeholder="e.g. ORD-12345"
                  class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
                  :disabled="detailsConfirmed"
                >
              </div>

              <!-- Amount field -->
              <div>
                <label
                  for="amount"
                  class="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1"
                >
                  Payment Amount <span class="tw-text-red-500">*</span>
                </label>
                <div class="tw-relative">
                  <span class="tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3 tw-text-slate-500 tw-text-sm">
                    £
                  </span>
                  <input
                    id="amount"
                    v-model="amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="0.00"
                    class="tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-pl-8 tw-pr-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
                    :disabled="detailsConfirmed"
                  >
                </div>
              </div>

              <!-- Continue button -->
              <button
                v-if="!detailsConfirmed"
                type="button"
                :disabled="!canContinue"
                :class="[
                  'tw-w-full tw-py-3 tw-px-4 tw-rounded-md tw-font-medium tw-text-sm tw-transition-colors',
                  canContinue
                    ? 'tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-shadow-sm'
                    : 'tw-bg-slate-200 tw-text-slate-400 tw-cursor-not-allowed'
                ]"
                @click="confirmDetails"
              >
                Continue to payment details
              </button>

              <!-- Edit button -->
              <button
                v-else
                type="button"
                class="tw-w-full tw-inline-flex tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-text-slate-700 tw-shadow-sm hover:tw-bg-slate-50"
                @click="editDetails"
              >
                Edit details
              </button>
            </div>
          </div>

          <!-- Customer details form (shown after confirming payment details) -->
          <PaymentForm
            v-if="detailsConfirmed"
            :reference="reference"
            :amount="parseFloat(amount)"
            @payment-success="handlePaymentSuccess"
            @payment-error="handlePaymentError"
            @payment-confirm="handlePaymentConfirm"
          />
        </section>

        <!-- Right: summary card -->
        <aside class="lg:tw-col-span-1">
          <div class="tw-bg-white tw-rounded-xl tw-shadow-md tw-p-6 tw-sticky tw-top-6">
            <h3 class="tw-text-base tw-font-semibold tw-text-slate-900 tw-mb-4">
              Payment Summary
            </h3>

            <div class="tw-space-y-3">
              <div class="tw-flex tw-justify-between tw-text-sm">
                <span class="tw-text-slate-600">Reference:</span>
                <span class="tw-font-medium tw-text-slate-900">
                  {{ reference || '—' }}
                </span>
              </div>

              <div class="tw-h-px tw-bg-slate-200" />

              <div class="tw-flex tw-justify-between tw-text-sm">
                <span class="tw-text-slate-600">Amount:</span>
                <span class="tw-font-semibold tw-text-slate-900 tw-text-lg">
                  {{ formatCurrency(amount) }}
                </span>
              </div>
            </div>

            <div class="tw-mt-6 tw-rounded-lg tw-bg-blue-50 tw-p-4">
              <div class="tw-flex tw-items-start tw-gap-3">
                <svg
                  class="tw-h-5 tw-w-5 tw-text-blue-600 tw-flex-shrink-0 tw-mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <div>
                  <p class="tw-text-xs tw-font-medium tw-text-blue-900">
                    Secure Payment
                  </p>
                  <p class="tw-mt-1 tw-text-xs tw-text-blue-700">
                    Your payment is processed securely using industry-standard encryption
                  </p>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>

      </template>
    </div>

    <!-- Success modal -->
    <Teleport to="body">
      <div
        v-if="showSuccessModal"
        class="tw-fixed tw-inset-0 tw-z-50 tw-overflow-y-auto"
      >
        <div class="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-p-4">
          <div
            class="tw-fixed tw-inset-0 tw-bg-slate-900 tw-bg-opacity-60 tw-transition-opacity"
            @click="closeSuccessModal"
          />
          <div class="tw-relative tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-max-w-md tw-w-full tw-p-8">
            <div class="tw-text-center">
              <div class="tw-mx-auto tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-50">
                <svg
                  class="tw-h-7 tw-w-7 tw-text-emerald-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h3 class="tw-mt-4 tw-text-xl tw-font-semibold tw-text-slate-900">
                Payment Successful
              </h3>
              <p class="tw-mt-2 tw-text-sm tw-text-slate-500">
                Thank you. Your payment has been processed successfully.
              </p>
              <p
                v-if="successReference"
                class="tw-mt-4 tw-text-sm tw-font-medium tw-text-slate-800"
              >
                Reference: <span class="tw-font-semibold">{{ successReference }}</span>
              </p>
              <button
                class="tw-mt-6 tw-w-full tw-inline-flex tw-justify-center tw-rounded-md tw-border tw-border-transparent tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2"
                @click="closeSuccessModal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, inject } from 'vue'
import { useToast } from 'vue-toastification'
import PaymentForm from './components/PaymentForm.vue'

export default {
  name: 'AdhocPaymentPage',
  components: {
    PaymentForm
  },
  setup() {
    const toast = useToast()
    const coreService = inject('coreService')

    // State
    const reference = ref('')
    const amount = ref('')
    const detailsConfirmed = ref(false)
    const showSuccessModal = ref(false)
    const successReference = ref('')
    const confirmLoading = ref(false)
    const confirmView = ref(null) // 'pending', 'success', 'failed'
    const confirmData = ref(null)
    let pollTimer = null

    // Check for query parameters
    onMounted(async () => {
      const urlParams = new URLSearchParams(window.location.search)
      const type = urlParams.get('type')
      const token = urlParams.get('token')

      // Handle payment confirmation callback
      if (type === 'confirm' && token) {
        await checkTransactionStatus(token)
        return
      }

      const refParam = urlParams.get('reference') || urlParams.get('ref')
      const amountParam = urlParams.get('amount')

      if (refParam) {
        reference.value = refParam
      }
      if (amountParam) {
        const parsed = parseFloat(amountParam)
        if (!isNaN(parsed) && parsed > 0) {
          amount.value = parsed.toFixed(2)
        }
      }
    })

    // Computed
    const canContinue = computed(() => {
      const hasRef = reference.value?.trim().length > 0
      const hasAmount = amount.value && parseFloat(amount.value) > 0
      return hasRef && hasAmount
    })

    const formatCurrency = (value) => {
      const numericValue = typeof value === 'string' ? parseFloat(value) : value
      if (!numericValue || isNaN(numericValue)) {
        return '£0.00'
      }
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(numericValue)
    }

    // Methods
    const confirmDetails = () => {
      if (!canContinue.value) {
        toast.error('Please enter both reference and amount')
        return
      }
      detailsConfirmed.value = true
    }

    const editDetails = () => {
      detailsConfirmed.value = false
    }

    // Check transaction status after payment redirect
    // status: 0 = pending, 1 = success, 2 = failed
    const checkTransactionStatus = async (token) => {
      confirmLoading.value = true
      confirmView.value = 'pending'
      try {
        const response = await coreService.checkAdhocTransaction(token)
        const status = parseInt(response.data?.status)
        confirmData.value = response.data

        if (status === 1) {
          confirmView.value = 'success'
          stopPolling()
        } else if (status === 0) {
          confirmView.value = 'pending'
          startPolling(token)
        } else {
          confirmView.value = 'failed'
          stopPolling()
        }
      } catch (err) {
        confirmView.value = 'failed'
        stopPolling()
      } finally {
        confirmLoading.value = false
      }
    }

    const startPolling = (token) => {
      stopPolling()
      pollTimer = setInterval(() => {
        checkTransactionStatus(token)
      }, 3000)
    }

    const stopPolling = () => {
      if (pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
      }
    }

    onUnmounted(() => {
      stopPolling()
    })

    const handlePaymentConfirm = (token) => {
      checkTransactionStatus(token)
    }

    const handlePaymentSuccess = (data) => {
      successReference.value = data?.reference || reference.value
      showSuccessModal.value = true
      toast.success('Payment processed successfully!')
    }

    const handlePaymentError = (message) => {
      toast.error(message || 'Payment failed. Please try again.')
    }

    const closeSuccessModal = () => {
      showSuccessModal.value = false
      // Reset form
      reference.value = ''
      amount.value = ''
      detailsConfirmed.value = false
      successReference.value = ''
    }

    return {
      reference,
      amount,
      detailsConfirmed,
      canContinue,
      showSuccessModal,
      successReference,
      confirmLoading,
      confirmView,
      confirmData,
      confirmDetails,
      editDetails,
      formatCurrency,
      handlePaymentSuccess,
      handlePaymentError,
      handlePaymentConfirm,
      closeSuccessModal
    }
  }
}
</script>

<style scoped></style>
